import { useState, FormEvent, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { openingHours } from '@/mocks/hours';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.reveal-item');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('Votre message a été envoyé avec succès !');
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormStatus('error');
      setFormMessage('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMessage('Le message ne doit pas dépasser 500 caractères.');
      return;
    }

    setFormStatus('loading');
    try {
      const payload = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9fbgdsjvhh956i3lmr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(text); } catch { /* raw text */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
        setFormStatus('error');
        setFormMessage('Une erreur est survenue. Veuillez réessayer.');
      } else {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : 'Une erreur est survenue. Veuillez réessayer.');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Une erreur réseau est survenue. Veuillez réessayer.');
    }
  };

  return (
    <Layout>
      {/* Clean elegant header */}
      <section className="w-full pt-28 md:pt-32 pb-12 md:pb-16 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 tracking-wider uppercase">Nous trouver</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-800 mb-4">Contact & Accès</h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
              Une question, une réservation, ou envie de nous rendre visite ? Nous sommes à votre écoute.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="w-full py-8 bg-background-100 border-y border-background-200/50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <a href="tel:+2250757006969" className="flex items-center gap-3 justify-center group">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300">
                <i className="ri-phone-line text-lg text-primary-500 group-hover:text-background-50 transition-colors" />
              </div>
              <div>
                <p className="text-xs text-foreground-500">Téléphone</p>
                <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">+225 07 57 00 69 69</p>
              </div>
            </a>
            <a href="mailto:contact@kdsbeauty.ci" className="flex items-center gap-3 justify-center group">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300">
                <i className="ri-mail-line text-lg text-primary-500 group-hover:text-background-50 transition-colors" />
              </div>
              <div>
                <p className="text-xs text-foreground-500">Email</p>
                <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">contact@kdsbeauty.ci</p>
              </div>
            </a>
            <div className="flex items-center gap-3 justify-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50">
                <i className="ri-map-pin-line text-lg text-primary-500" />
              </div>
              <div>
                <p className="text-xs text-foreground-500">Adresse</p>
                <p className="text-sm font-medium text-foreground-800">Angré Château, Abidjan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 max-w-6xl mx-auto">
            {/* Info Column */}
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-5/12"
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-800 mb-8">Nos coordonnées</h2>

              <div className="space-y-7 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50">
                    <i className="ri-map-pin-line text-lg text-primary-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">Adresse</h4>
                    <p className="text-sm text-foreground-600">Angré Château, Carrefour Cabri</p>
                    <p className="text-sm text-foreground-600">Abidjan, Côte d'Ivoire</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50">
                    <i className="ri-phone-line text-lg text-primary-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">Téléphone</h4>
                    <a href="tel:+2250715021015" className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors mb-0.5">+225 07 15 02 10 15</a>
                    <a href="tel:+2250757006969" className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors mb-0.5">+225 07 57 00 69 69</a>
                    <a href="tel:+2250506005550" className="block text-sm text-foreground-600 hover:text-primary-500 transition-colors">+225 05 06 00 55 50</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-green-50">
                    <i className="ri-whatsapp-line text-lg text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">WhatsApp</h4>
                    <a href="https://wa.me/2250757006969" target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-foreground-600 hover:text-green-600 transition-colors">
                      +225 07 57 00 69 69
                    </a>
                    <p className="text-xs text-foreground-500 mt-1">Réponse rapide garantie</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50">
                    <i className="ri-mail-line text-lg text-primary-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">Email</h4>
                    <a href="mailto:contact@kdsbeauty.ci" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">contact@kdsbeauty.ci</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50">
                    <i className="ri-share-line text-lg text-primary-500" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-3">Réseaux sociaux</h4>
                    <div className="flex items-center gap-3">
                      <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-300">
                        <i className="ri-tiktok-fill text-lg" />
                      </a>
                      <a href="https://instagram.com" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-all duration-300">
                        <i className="ri-instagram-line text-lg" />
                      </a>
                      <a href="https://wa.me/2250757006969" target="_blank" rel="noopener noreferrer nofollow" className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-green-500 hover:text-background-50 transition-all duration-300">
                        <i className="ri-whatsapp-line text-lg" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl p-6 border border-background-200/60">
                <h3 className="font-heading text-lg text-foreground-800 mb-4">Horaires d'ouverture</h3>
                <ul className="space-y-3">
                  {openingHours.map((item) => (
                    <li key={item.day} className="flex justify-between text-sm">
                      <span className="text-foreground-600">{item.day}</span>
                      <span className={`font-semibold ${item.hours === 'Fermé' ? 'text-accent-500' : 'text-foreground-800'}`}>{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick WhatsApp CTA */}
              <a
                href="https://wa.me/2250757006969?text=Bonjour%20KD'S%20BEAUTY%20!%20Je%20souhaite%20prendre%20rendez-vous."
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-6 flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-green-600 text-white hover:bg-green-700 transition-all duration-300"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-lg" />
                </div>
                <span className="text-sm font-medium">Réserver par WhatsApp</span>
              </a>
            </div>

            {/* Form Column */}
            <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 lg:w-7/12"
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', transitionDelay: '150ms' }}
            >
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 lg:p-10 border border-background-200/60">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-800 mb-2">Envoyez-nous un message</h2>
                <p className="text-sm text-foreground-500 mb-8">Remplissez le formulaire ci-dessous, nous vous répondrons dans les plus brefs délais.</p>

                <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-5">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">Nom complet *</label>
                      <input
                        type="text" id="name" name="name" required
                        value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Votre nom"
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email *</label>
                      <input
                        type="email" id="email" name="email" required
                        value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="votre@email.com"
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">Téléphone</label>
                      <input
                        type="tel" id="phone" name="phone"
                        value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Votre numéro"
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">Motif</label>
                      <select
                        id="subject" name="subject"
                        value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                      >
                        <option value="">Sélectionnez un motif</option>
                        <option value="Rendez-vous">Prise de rendez-vous</option>
                        <option value="Information">Demande d'information</option>
                        <option value="Carte cadeau">Carte cadeau</option>
                        <option value="Réclamation">Réclamation</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">Message *</label>
                    <textarea
                      id="message" name="message" required
                      rows={5} maxLength={500}
                      value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre demande..."
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200/70 rounded-xl text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500 caractères</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="inline-flex items-center justify-center px-8 py-3.5 rounded-full text-base font-medium bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 w-full sm:w-auto"
                  >
                    {formStatus === 'loading' ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 p-4 bg-secondary-50 border border-secondary-200/50 rounded-xl">
                      <i className="ri-check-line text-secondary-600" />
                      <p className="text-sm text-secondary-700">{formMessage}</p>
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-4 bg-accent-50 border border-accent-200/50 rounded-xl">
                      <i className="ri-error-warning-line text-accent-600" />
                      <p className="text-sm text-accent-700">{formMessage}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="reveal-item opacity-0 translate-y-6 transition-all duration-700 mt-16 max-w-6xl mx-auto"
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div className="rounded-2xl overflow-hidden border border-background-200/60 aspect-[16/7] min-h-[300px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3972.4438888888888!2d-3.9694444!3d5.3488889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNcKwMjAnNTYuMCBOIDPCsDU4JzEwLjAgVw!5e0!3m2!1sfr!2sci!4v1690000000000"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="KD'S BEAUTY - Localisation"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}